import React, { useState, useEffect, useReducer, useRef } from 'react';
import SetupFlow from './components/SetupFlow';
import GameView from './components/GameView';
import SamanthaTerminal from './components/SamanthaTerminal';
import { ACHIEVEMENTS } from './data/achievements_db';
import { signInWithGoogle, signInWithFacebook, saveGameStateToCloud, loadGameStateFromCloud } from './firebase';

const GRID_SIZE = 15;

const generateMap = () => {
    return Array.from({ length: GRID_SIZE }, (_, y) =>
        Array.from({ length: GRID_SIZE }, (_, x) => ({
            x, y,
            type: (x === 7 && y === 7) ? 'PLAYER' :
                  (Math.random() > 0.96) ? 'WILDLIFE' :
                  (Math.random() > 0.90) ? 'HOSTILE' :
                  (Math.random() > 0.94) ? 'RESOURCE' : 'EMPTY',
            scanned: (x === 7 && y === 7),
            looted: false
        }))
    );
};

// --- RANDOM EVENT SYSTEM ---
// Events fire after actions. Choices resolve differently based on traits, defense, and resources.
export const RANDOM_EVENTS = [
    {
        id: 'hostile_contact',
        name: 'HOSTILE_CONTACT',
        description: 'Perimeter sensors detect approaching hostiles. Raiders are probing your compound defenses.',
        samanthaQuote: "Commander, I'm reading heat signatures at the perimeter. Multiple contacts. Recommend immediate defensive posture.",
        probability: 0.11,
        minDay: 5,
        choices: [
            {
                id: 'defend',
                label: 'Hold Position — Defend the Perimeter',
                log: (res, state) => {
                    const ammoStrong = res.ammo >= 15;
                    if (res.defense >= 5) return 'REPELLED. Heavy fortifications routed the raiders. Unit morale boosted.';
                    if (res.defense >= 2 && ammoStrong) return 'SUPPRESSED. Defense + firepower combo drove raiders back. Minimal losses. Ammo well spent.';
                    if (res.defense >= 2) return 'SKIRMISH. Partial defenses held. Ammo spent. Raiders took some supplies before retreating.';
                    if (ammoStrong) return 'FIREPOWER HELD THE LINE. No fortifications — but heavy fire kept them from breaching. Costly.';
                    return 'BREACH. Compound overwhelmed. Major supplies looted. Fortify and stock ammo before next contact.';
                },
                effect: (res, state) => {
                    const ammoStrong = res.ammo >= 15;
                    if (res.defense >= 5) return { ...res, ammo: Math.max(0, res.ammo - 3), morale: Math.min(100, res.morale + 10) };
                    if (res.defense >= 2 && ammoStrong) return { ...res, ammo: Math.max(0, res.ammo - 8), morale: Math.min(100, res.morale + 6) };
                    if (res.defense >= 2) return { ...res, ammo: Math.max(0, res.ammo - 8), food: Math.max(0, res.food - 10), morale: Math.max(0, res.morale - 10) };
                    if (ammoStrong) return { ...res, ammo: Math.max(0, res.ammo - 14), food: Math.max(0, res.food - 16), morale: Math.max(0, res.morale - 14) };
                    return { ...res, food: Math.max(0, res.food - 30), water: Math.max(0, res.water - 18), ammo: Math.max(0, res.ammo - 10), morale: Math.max(0, res.morale - 30) };
                }
            },
            {
                id: 'warning_shots',
                label: 'Warning Shots — Controlled Show of Force',
                log: (res) => res.ammo >= 8
                    ? 'DETERRED. Controlled burst communicated lethal capability. Raiders withdrew without engaging.'
                    : 'BLUFF CALLED. Too few rounds to sustain fire. Raiders exploited the hesitation.',
                effect: (res) => {
                    if (res.ammo >= 8) return { ...res, ammo: Math.max(0, res.ammo - 8), morale: Math.min(100, res.morale + 8) };
                    return { ...res, ammo: Math.max(0, res.ammo - 3), food: Math.max(0, res.food - 20), morale: Math.max(0, res.morale - 18) };
                }
            },
            {
                id: 'evade',
                label: 'Go Dark — Silent Evasion Protocol',
                log: (res, state) => {
                    const hasAgile = state.family.some(m => m.traits.includes('agile'));
                    return hasAgile ? 'GHOST PROTOCOL. Agile unit led silent evasion. Raiders moved on unaware.' : 'EVASION. Powered down all systems. Raiders damaged outer perimeter before leaving.';
                },
                effect: (res, state) => {
                    const hasAgile = state.family.some(m => m.traits.includes('agile'));
                    if (hasAgile) return { ...res, fuel: Math.max(0, res.fuel - 3), morale: Math.max(0, res.morale - 5) };
                    return { ...res, fuel: Math.max(0, res.fuel - 8), defense: Math.max(0, res.defense - 1), morale: Math.max(0, res.morale - 18) };
                }
            }
        ]
    },
    {
        id: 'illness_outbreak',
        name: 'PATHOGEN_DETECTED',
        description: 'A family member has fallen ill. Fever rising. High risk of spreading to the entire unit.',
        samanthaQuote: "I'm detecting elevated biometrics across the unit. Someone is very sick, Commander. Act quickly.",
        probability: 0.08,
        minDay: 3,
        choices: [
            {
                id: 'treat',
                label: 'Administer Medical Treatment',
                log: (res, state) => {
                    const hasMedic = state.family.some(m => m.traits.includes('medic'));
                    const cost = hasMedic ? 1 : 3;
                    if (res.meds >= cost) return `TREATED. ${hasMedic ? 'Unit medic administered efficient treatment.' : 'Medical supplies consumed.'} Patient recovering.`;
                    return 'CRITICAL: Insufficient meds. Illness spreading through the compound. Unit morale collapsing.';
                },
                effect: (res, state) => {
                    const hasMedic = state.family.some(m => m.traits.includes('medic'));
                    const cost = hasMedic ? 1 : 3;
                    if (res.meds >= cost) return { ...res, meds: res.meds - cost, morale: Math.min(100, res.morale + 5) };
                    return { ...res, morale: Math.max(0, res.morale - 35), water: Math.max(0, res.water - 12) };
                }
            },
            {
                id: 'quarantine',
                label: 'Quarantine & Monitor the Situation',
                log: () => 'QUARANTINE ESTABLISHED. Spread contained for now. Morale impacted by isolation.',
                effect: (res) => ({ ...res, morale: Math.max(0, res.morale - 18), water: Math.max(0, res.water - 8) })
            }
        ]
    },
    {
        id: 'water_contamination',
        name: 'WATER_ALERT',
        description: 'Sensor array reports contamination in the water supply. Do not consume. Immediate action required.',
        samanthaQuote: "Commander, I'm flagging anomalous chemical compounds in your water filtration output. This is serious.",
        probability: 0.07,
        minDay: 1,
        choices: [
            {
                id: 'purify',
                label: 'Emergency Purification Protocol',
                log: (res, state) => {
                    const hasEngineer = state.family.some(m => m.traits.includes('engineer'));
                    return `PURIFIED. ${hasEngineer ? 'Engineer optimized the purification process.' : 'Emergency fuel reserves used.'} Water supply restored.`;
                },
                effect: (res, state) => {
                    const hasEngineer = state.family.some(m => m.traits.includes('engineer'));
                    return { ...res, water: Math.max(0, res.water - (hasEngineer ? 5 : 18)), fuel: Math.max(0, res.fuel - (hasEngineer ? 2 : 6)) };
                }
            },
            {
                id: 'ration',
                label: 'Emergency Rationing — Conserve What Remains',
                log: () => 'RATIONING. Water severely restricted. Unit morale suffering. Find an alternative source.',
                effect: (res) => ({ ...res, morale: Math.max(0, res.morale - 15) })
            }
        ]
    },
    {
        id: 'supply_cache',
        name: 'CACHE_DISCOVERED',
        description: 'Exterior sensors flagged an untouched pre-war supply cache nearby. Opportunity window is limited.',
        samanthaQuote: "Commander, I'm reading intact seals on a cache 200 meters out. Pre-war military surplus. Window closing.",
        probability: 0.07,
        minDay: 2,
        choices: [
            {
                id: 'retrieve',
                label: 'Send Team to Retrieve Cache',
                log: (res, state) => {
                    const hasHunter = state.family.some(m => m.traits.includes('hunter'));
                    const hasAgile = state.family.some(m => m.traits.includes('agile'));
                    return `RECOVERED. ${(hasHunter || hasAgile) ? 'Field specialist maximized the haul.' : 'Standard extraction.'} +Food +Water +Meds +Ammo`;
                },
                effect: (res, state) => {
                    const hasHunter = state.family.some(m => m.traits.includes('hunter'));
                    const hasAgile = state.family.some(m => m.traits.includes('agile'));
                    const mul = (hasHunter || hasAgile) ? 1.6 : 1;
                    return { ...res, food: res.food + Math.floor(22 * mul), water: res.water + Math.floor(16 * mul), meds: res.meds + Math.floor(3 * mul), ammo: res.ammo + Math.floor(5 * mul), morale: Math.min(100, res.morale + 12) };
                }
            },
            {
                id: 'mark',
                label: 'Mark Location — Too Dangerous Right Now',
                log: () => 'MARKED. Cache location logged. Situation noted for future retrieval.',
                effect: (res) => ({ ...res, morale: Math.min(100, res.morale + 2) })
            }
        ]
    },
    {
        id: 'stranger_arrives',
        name: 'UNKNOWN_CONTACT',
        description: 'A survivor approaches your compound perimeter. Armed but not aggressive. Requesting asylum.',
        samanthaQuote: "Commander, I detect a lone survivor at the gate. Biometrics suggest malnourished but non-hostile.",
        probability: 0.06,
        minDay: 4,
        choices: [
            {
                id: 'help',
                label: 'Grant Temporary Shelter',
                log: (res, state) => {
                    const hasCharismatic = state.family.some(m => m.traits.includes('charismatic'));
                    return `ASSISTED. ${hasCharismatic ? "Charismatic unit member's welcome electrified morale." : 'Human connection lifted unit spirits.'} +Morale`;
                },
                effect: (res, state) => {
                    const hasCharismatic = state.family.some(m => m.traits.includes('charismatic'));
                    return { ...res, food: Math.max(0, res.food - 6), water: Math.max(0, res.water - 4), morale: Math.min(100, res.morale + (hasCharismatic ? 24 : 14)) };
                }
            },
            {
                id: 'turn_away',
                label: 'Deny Entry — Compound Security Protocol',
                log: () => 'DENIED. Security maintained. The decision weighs on the unit.',
                effect: (res) => ({ ...res, morale: Math.max(0, res.morale - 18) })
            }
        ]
    },
    {
        id: 'equipment_failure',
        name: 'SYSTEM_MALFUNCTION',
        description: 'Critical compound systems failing. Power fluctuations detected. Life support stability compromised.',
        samanthaQuote: "Commander, diagnostics show cascading system failures. Your compound is degrading. I need your decision.",
        probability: 0.07,
        minDay: 7,
        choices: [
            {
                id: 'repair',
                label: 'Emergency Field Repair',
                log: (res, state) => {
                    const hasMechanic = state.family.some(m => m.traits.includes('mechanic'));
                    return hasMechanic ? 'REPAIRED. Unit mechanic restored systems with remarkable efficiency.' : 'REPAIRED. Improvised repair. Significant fuel cost. Systems stabilized.';
                },
                effect: (res, state) => {
                    const hasMechanic = state.family.some(m => m.traits.includes('mechanic'));
                    return { ...res, fuel: Math.max(0, res.fuel - (hasMechanic ? 3 : 14)) };
                }
            },
            {
                id: 'bypass',
                label: 'Bypass Non-Critical Systems',
                log: () => 'BYPASSED. Non-essential systems offline. Compound integrity slightly reduced.',
                effect: (res) => ({ ...res, fuel: Math.max(0, res.fuel - 4), defense: Math.max(0, res.defense - 1) })
            }
        ]
    },
    {
        id: 'dust_storm',
        name: 'ATMOSPHERIC_EVENT',
        description: 'Severe dust storm approaching. Visibility zero. All exterior operations suspended immediately.',
        samanthaQuote: "Commander, satellite imagery shows a major particulate event. Full lockdown recommended. Now.",
        probability: 0.07,
        minDay: 1,
        choices: [
            {
                id: 'hunker',
                label: 'Full Compound Lockdown',
                log: () => 'LOCKDOWN. Storm passed. Air filtration burned fuel. Confinement strained morale.',
                effect: (res) => ({ ...res, morale: Math.max(0, res.morale - 10), fuel: Math.max(0, res.fuel - 6) })
            },
            {
                id: 'scavenge_storm',
                label: 'Send Scout — Use Storm as Cover',
                log: (res, state) => {
                    const hasAgile = state.family.some(m => m.traits.includes('agile'));
                    return hasAgile ? 'GUTSY CALL. Agile scout used storm as cover. Abandoned supplies found. Bold move.' : 'FAILED. Scout injured navigating the storm. No supplies recovered.';
                },
                effect: (res, state) => {
                    const hasAgile = state.family.some(m => m.traits.includes('agile'));
                    if (hasAgile) return { ...res, food: res.food + 20, morale: Math.min(100, res.morale + 10) };
                    return { ...res, morale: Math.max(0, res.morale - 22), meds: Math.max(0, res.meds - 1) };
                }
            }
        ]
    },
    {
        id: 'radio_signal',
        name: 'TRANSMISSION_DETECTED',
        description: 'Radio burst on emergency frequencies. Another survivor group is signaling. Intentions unknown.',
        samanthaQuote: "Commander, I've decrypted a radio signal. Another compound, approximately 12 kilometers east. Alive.",
        probability: 0.06,
        minDay: 5,
        choices: [
            {
                id: 'respond',
                label: 'Establish Contact',
                log: (res, state) => {
                    const hasCharismatic = state.family.some(m => m.traits.includes('charismatic'));
                    return hasCharismatic ? 'ALLIANCE FORMED. Charismatic diplomat established a trade route. +Food +Morale' : 'CONTACT. Communication established. Knowing others survive boosts morale.';
                },
                effect: (res, state) => {
                    const hasCharismatic = state.family.some(m => m.traits.includes('charismatic'));
                    if (hasCharismatic) return { ...res, morale: Math.min(100, res.morale + 28), food: res.food + 15 };
                    return { ...res, morale: Math.min(100, res.morale + 16) };
                }
            },
            {
                id: 'ignore',
                label: 'Maintain Radio Silence',
                log: () => 'SILENT. Signal ignored. Location stays dark. The silence weighs on the unit.',
                effect: (res) => ({ ...res, morale: Math.max(0, res.morale - 8) })
            }
        ]
    },
    {
        id: 'morale_crisis',
        name: 'UNIT_FRACTURE',
        description: 'Conflict erupting inside the compound. Arguments escalating. Someone is talking about leaving.',
        samanthaQuote: "Commander, I'm detecting critical psychological stress across the unit. This is an emergency.",
        probability: 0.12,
        minDay: 3,
        conditions: (state) => state.resources.morale < 45,
        choices: [
            {
                id: 'rally',
                label: 'Emergency Unit Rally — Extra Rations',
                log: (res, state) => {
                    const hasLeader = state.family.some(m => m.traits.includes('leader'));
                    return hasLeader ? "RALLIED. The leader's speech reignited the unit's will to survive." : 'RALLIED. Extra rations distributed. Morale partially restored.';
                },
                effect: (res, state) => {
                    const hasLeader = state.family.some(m => m.traits.includes('leader'));
                    return { ...res, food: Math.max(0, res.food - 6), morale: Math.min(100, res.morale + (hasLeader ? 35 : 20)) };
                }
            },
            {
                id: 'discipline',
                label: 'Maintain Discipline — No Concessions',
                log: () => 'HARDLINE. No concessions. Unit tensions dangerously high. This cannot continue.',
                effect: (res) => ({ ...res, morale: Math.max(0, res.morale - 22) })
            }
        ]
    },
    {
        id: 'trade_caravan',
        name: 'MERCHANT_CONTACT',
        description: 'Mobile trader spotted at the outer perimeter. High-value goods available. Short window.',
        samanthaQuote: "Commander, I'm tracking a trade caravan with medical supplies and food. Opportunity closing fast.",
        probability: 0.06,
        minDay: 6,
        conditions: (state) => state.resources.fuel >= 10,
        choices: [
            {
                id: 'trade',
                label: 'Trade Fuel for Medical Supplies',
                log: (res, state) => {
                    const hasCharismatic = state.family.some(m => m.traits.includes('charismatic'));
                    return hasCharismatic ? 'TRADE COMPLETE. Charismatic negotiator secured exceptional rates. +Food +Meds' : 'TRADE COMPLETE. Fair exchange executed. +Food +Meds';
                },
                effect: (res, state) => {
                    const hasCharismatic = state.family.some(m => m.traits.includes('charismatic'));
                    const mul = hasCharismatic ? 1.5 : 1;
                    return { ...res, fuel: Math.max(0, res.fuel - 12), food: res.food + Math.floor(22 * mul), meds: res.meds + Math.floor(4 * mul) };
                }
            },
            {
                id: 'decline',
                label: 'Decline — Operational Security',
                log: () => 'DECLINED. Trader contact avoided. Location stays secure. Unit disappointed by the missed opportunity.',
                effect: (res) => ({ ...res, morale: Math.max(0, res.morale - 6) })
            }
        ]
    },
    {
        id: 'wildlife',
        name: 'WILDLIFE_DETECTED',
        description: 'Motion sensors detect a large pack of animals in the outer zone. Significant food opportunity.',
        samanthaQuote: "Commander, wildlife activity detected 300 meters out. A sizable pack. Your call on pursuit.",
        probability: 0.07,
        minDay: 2,
        conditions: (state) => state.resources.ammo > 0,
        choices: [
            {
                id: 'hunt',
                label: 'Deploy Hunting Party',
                log: (res, state) => {
                    const hasHunter = state.family.some(m => m.traits.includes('hunter'));
                    if (hasHunter) return 'HUNT SUCCESSFUL. Experienced hunter brought back exceptional game. Unit morale boosted.';
                    return Math.random() > 0.45 ? 'HUNT SUCCESS. Lucky shot. Food secured.' : 'HUNT FAILED. Animals evaded. Ammo wasted. No hunter in the unit.';
                },
                effect: (res, state) => {
                    const hasHunter = state.family.some(m => m.traits.includes('hunter'));
                    if (hasHunter) return { ...res, food: res.food + 38, ammo: Math.max(0, res.ammo - 2), morale: Math.min(100, res.morale + 10) };
                    const success = Math.random() > 0.45;
                    if (success) return { ...res, food: res.food + 14, ammo: Math.max(0, res.ammo - 6) };
                    return { ...res, ammo: Math.max(0, res.ammo - 6), morale: Math.max(0, res.morale - 8) };
                }
            },
            {
                id: 'pass',
                label: 'Hold Position — Conserve Ammunition',
                log: () => 'PASSED. Wildlife contact avoided. Conserving ammunition for raids. Unit restless from inaction.',
                effect: (res) => ({ ...res, morale: Math.max(0, res.morale - 5) })
            }
        ]
    },
    {
        id: 'structural_damage',
        name: 'COMPOUND_INTEGRITY_LOSS',
        description: 'Compound structure showing critical stress fractures. Risk of section collapse. Immediate attention needed.',
        samanthaQuote: "Commander, structural sensors show multiple failure points. This is a critical situation.",
        probability: 0.06,
        minDay: 10,
        choices: [
            {
                id: 'repair_structure',
                label: 'Emergency Structural Repair',
                log: (res, state) => {
                    const hasExpert = state.family.some(m => m.traits.includes('engineer') || m.traits.includes('mechanic'));
                    return hasExpert ? 'REPAIRED. Technical expert produced superior structural repair. +Defense' : 'REPAIRED. Costly improvised repair. Compound stabilized. +Defense';
                },
                effect: (res, state) => {
                    const hasExpert = state.family.some(m => m.traits.includes('engineer') || m.traits.includes('mechanic'));
                    return { ...res, fuel: Math.max(0, res.fuel - (hasExpert ? 4 : 12)), defense: res.defense + (hasExpert ? 2 : 1) };
                }
            },
            {
                id: 'abandon_section',
                label: 'Seal and Abandon Damaged Section',
                log: () => 'SECTION SEALED. Compound footprint reduced. Defense capability decreased.',
                effect: (res) => ({ ...res, morale: Math.max(0, res.morale - 12), defense: Math.max(0, res.defense - 1) })
            }
        ]
    },
    {
        id: 'ammo_barter',
        name: 'ARMS_MERCHANT',
        description: 'A hardened trader has set up outside the perimeter. They deal exclusively in ammunition and have premium supplies.',
        samanthaQuote: "Commander, armed trader at the gate. High-quality goods — they want ammo, not fuel. Your call.",
        probability: 0.07,
        minDay: 4,
        conditions: (state) => state.resources.ammo >= 8,
        choices: [
            {
                id: 'trade_ammo_food',
                label: 'Trade 10 Ammo — Food & Water Supplies',
                log: (res, state) => {
                    const hasCharismatic = state.family.some(m => m.traits.includes('charismatic'));
                    return `TRADE COMPLETE. ${hasCharismatic ? 'Negotiator secured above-market rations.' : 'Fair trade executed.'} -10 Ammo +Food +Water`;
                },
                effect: (res, state) => {
                    const mul = state.family.some(m => m.traits.includes('charismatic')) ? 1.45 : 1;
                    return { ...res, ammo: Math.max(0, res.ammo - 10), food: res.food + Math.floor(32 * mul), water: res.water + Math.floor(22 * mul) };
                }
            },
            {
                id: 'trade_ammo_meds',
                label: 'Trade 8 Ammo — Medical Cache',
                log: (res, state) => {
                    const hasCharismatic = state.family.some(m => m.traits.includes('charismatic'));
                    return `TRADE COMPLETE. ${hasCharismatic ? 'Negotiated extra supplies.' : 'Medical supplies secured.'} -8 Ammo +Meds +Fuel`;
                },
                effect: (res, state) => {
                    const mul = state.family.some(m => m.traits.includes('charismatic')) ? 1.45 : 1;
                    return { ...res, ammo: Math.max(0, res.ammo - 8), meds: res.meds + Math.floor(7 * mul), fuel: res.fuel + Math.floor(12 * mul), morale: Math.min(100, res.morale + 8) };
                }
            },
            {
                id: 'decline_barter',
                label: 'Decline — Ammunition Is Not for Sale',
                log: () => 'DECLINED. Trader moved on. Ammunition stockpile preserved.',
                effect: (res) => ({ ...res })
            }
        ]
    },
    {
        id: 'tribute_demand',
        name: 'ARMED_EXTORTION',
        description: 'A coordinated raider faction has broadcast a demand: pay ammunition tribute or face assault. They know your location.',
        samanthaQuote: "Commander, I'm intercepting organized comms. This isn't random. They've been watching us. They want ammo.",
        probability: 0.07,
        minDay: 12,
        choices: [
            {
                id: 'pay_tribute',
                label: 'Pay the Tribute — 12 Ammo',
                log: () => 'PAID. Tribute handed over. Group moved on. Intelligence suggests they will return with higher demands.',
                effect: (res) => ({ ...res, ammo: Math.max(0, res.ammo - 12), morale: Math.max(0, res.morale - 14) })
            },
            {
                id: 'armed_refusal',
                label: 'Refuse — Stand and Fight',
                log: (res, state) => {
                    const hasStrong = state.family.some(m => m.traits.includes('strong'));
                    const wellArmed = res.ammo >= 16 && (hasStrong || res.defense >= 3);
                    return wellArmed
                        ? 'REPELLED. Firepower and fortitude made the cost too high. They withdrew. Reputation established.'
                        : 'COSTLY STAND. Held position but took significant losses. Ammo depleted. Compound damaged.';
                },
                effect: (res, state) => {
                    const hasStrong = state.family.some(m => m.traits.includes('strong'));
                    const wellArmed = res.ammo >= 16 && (hasStrong || res.defense >= 3);
                    if (wellArmed) return { ...res, ammo: Math.max(0, res.ammo - 10), morale: Math.min(100, res.morale + 18), defense: res.defense + 1 };
                    return { ...res, ammo: Math.max(0, res.ammo - 14), food: Math.max(0, res.food - 24), morale: Math.max(0, res.morale - 22) };
                }
            },
            {
                id: 'negotiate_tribute',
                label: 'Counter-Negotiate the Terms',
                log: (res, state) => {
                    const hasCharismatic = state.family.some(m => m.traits.includes('charismatic'));
                    return hasCharismatic
                        ? 'NEGOTIATED. Charismatic diplomat reduced tribute to 5 ammo and established terms.'
                        : 'FAILED NEGOTIATION. No diplomat in the unit. They took more than asked.';
                },
                effect: (res, state) => {
                    const hasCharismatic = state.family.some(m => m.traits.includes('charismatic'));
                    if (hasCharismatic) return { ...res, ammo: Math.max(0, res.ammo - 5), morale: Math.max(0, res.morale - 5) };
                    return { ...res, ammo: Math.max(0, res.ammo - 18), food: Math.max(0, res.food - 14), morale: Math.max(0, res.morale - 18) };
                }
            }
        ]
    },
    {
        id: 'gun_runner',
        name: 'GUN_RUNNER_CONTACT',
        description: 'A black market weapons dealer is passing through the sector. Rare opportunity to significantly expand your arsenal.',
        samanthaQuote: "Commander, I'm tracking an unregistered vehicle. Weapons dealer. High risk, high value. Window is 20 minutes.",
        probability: 0.05,
        minDay: 8,
        conditions: (state) => state.resources.budget >= 8000 || state.resources.fuel >= 20,
        choices: [
            {
                id: 'buy_ammo_bulk',
                label: 'Buy Bulk Ammunition — $8,000',
                log: (res, state) => {
                    return res.budget >= 8000
                        ? 'PURCHASED. Black market bulk ammo secured. Arsenal significantly expanded.'
                        : 'INSUFFICIENT FUNDS. Could not complete purchase.';
                },
                effect: (res) => {
                    if (res.budget >= 8000) return { ...res, budget: res.budget - 8000, ammo: res.ammo + 35 };
                    return res;
                }
            },
            {
                id: 'trade_fuel_ammo',
                label: 'Trade 20 Fuel for Ammunition',
                log: (res) => res.fuel >= 20
                    ? 'TRADED. Fuel exchanged for ammunition stockpile. Mobile capacity reduced.'
                    : 'INSUFFICIENT FUEL. Trade failed.',
                effect: (res) => {
                    if (res.fuel >= 20) return { ...res, fuel: Math.max(0, res.fuel - 20), ammo: res.ammo + 25 };
                    return res;
                }
            },
            {
                id: 'pass_on_dealer',
                label: 'Pass — Too Risky',
                log: () => 'PASSED. Dealer moved on. Operational security maintained.',
                effect: (res) => ({ ...res })
            }
        ]
    },
    {
        id: 'spoiled_supplies',
        name: 'SUPPLY_CONTAMINATION',
        description: 'Heat and humidity have spoiled a portion of the food stores. Mold, rot, vermin — standard collapse-world problems.',
        samanthaQuote: "Commander, contamination sensors in the storage bay are alarming. Some supplies are compromised. Act now.",
        probability: 0.09,
        minDay: 2,
        conditions: (state) => state.resources.food > 15,
        choices: [
            {
                id: 'salvage',
                label: 'Salvage What You Can',
                log: (res, state) => {
                    const hasMedic = state.family.some(m => m.traits.includes('medic'));
                    return hasMedic ? 'SALVAGE COMPLETE. Medic identified safe portions. Minimal loss.' : 'SALVAGE COMPLETE. Significant spoilage discarded. What remains is safe.';
                },
                effect: (res, state) => {
                    const hasMedic = state.family.some(m => m.traits.includes('medic'));
                    const loss = hasMedic ? Math.floor(res.food * 0.08) : Math.floor(res.food * 0.18);
                    return { ...res, food: Math.max(0, res.food - loss), morale: Math.max(0, res.morale - 6) };
                }
            },
            {
                id: 'purge_stores',
                label: 'Purge All Compromised Stores',
                log: () => 'PURGED. Contaminated supplies destroyed. No risk of illness. The loss is real.',
                effect: (res) => ({ ...res, food: Math.max(0, res.food - Math.floor(res.food * 0.24)), morale: Math.max(0, res.morale - 14) })
            }
        ]
    },
    {
        id: 'fuel_leak',
        name: 'FUEL_SYSTEM_BREACH',
        description: 'A slow fuel leak detected in the storage tanks. Already lost gallons. Pressure shows it is worsening.',
        samanthaQuote: "Commander, fuel pressure sensors show a consistent drop. You are hemorrhaging reserves. Find and fix it.",
        probability: 0.08,
        minDay: 3,
        conditions: (state) => state.resources.fuel > 8,
        choices: [
            {
                id: 'patch_leak',
                label: 'Emergency Patch — Stop the Bleed',
                log: (res, state) => {
                    const hasMechanic = state.family.some(m => m.traits.includes('mechanic'));
                    return hasMechanic ? 'PATCHED. Mechanic isolated and sealed the breach. Minimal loss.' : 'PATCHED. Improvised seal held. Fuel loss contained. For now.';
                },
                effect: (res, state) => {
                    const hasMechanic = state.family.some(m => m.traits.includes('mechanic'));
                    return { ...res, fuel: Math.max(0, res.fuel - (hasMechanic ? 4 : 10)) };
                }
            },
            {
                id: 'transfer_reserves',
                label: 'Emergency Transfer to Secondary Containers',
                log: () => 'TRANSFERRED. Fuel moved to backup containers. Some spilled in the rush. Leak source still unknown.',
                effect: (res) => ({ ...res, fuel: Math.max(0, res.fuel - 7), morale: Math.max(0, res.morale - 8) })
            }
        ]
    },
    {
        id: 'cold_snap',
        name: 'TEMPERATURE_COLLAPSE',
        description: 'Overnight temperature dropped severely. Compound heating is burning fuel fast. Unit health at risk if not addressed.',
        samanthaQuote: "Commander, external temperature is plummeting. Internal heating is struggling to compensate. I recommend action.",
        probability: 0.07,
        minDay: 4,
        choices: [
            {
                id: 'heat_up',
                label: 'Burn Fuel — Keep the Compound Warm',
                log: () => 'HEATED. Cold snap survived. Fuel reserves took a hit. Unit health preserved.',
                effect: (res) => ({ ...res, fuel: Math.max(0, res.fuel - 9), morale: Math.min(100, res.morale + 4) })
            },
            {
                id: 'ration_heat',
                label: 'Ration Heat — Endure the Cold',
                log: (res, state) => {
                    const hasStrong = state.family.some(m => m.traits.includes('strong'));
                    return hasStrong ? 'ENDURED. Strong unit members rallied the group through the cold. Minimal impact.' : 'ENDURED. Cold night wore the unit down. Morale and food consumption both spiked.';
                },
                effect: (res, state) => {
                    const hasStrong = state.family.some(m => m.traits.includes('strong'));
                    if (hasStrong) return { ...res, morale: Math.max(0, res.morale - 8) };
                    return { ...res, food: Math.max(0, res.food - 14), morale: Math.max(0, res.morale - 20), meds: Math.max(0, res.meds - 1) };
                }
            }
        ]
    },
    {
        id: 'lost_scout',
        name: 'SCOUT_OVERDUE',
        description: 'A unit member sent on a routine patrol has not returned. Radio contact lost 4 hours ago. Search or wait?',
        samanthaQuote: "Commander, I've lost telemetry on one of your unit members. Last known position was sector perimeter. This is concerning.",
        probability: 0.07,
        minDay: 5,
        choices: [
            {
                id: 'search_party',
                label: 'Send a Search Party Immediately',
                log: (res, state) => {
                    const hasAgile = state.family.some(m => m.traits.includes('agile'));
                    return hasAgile
                        ? 'RECOVERED. Agile scouts found the missing member quickly. Injured but alive. Small cache recovered.'
                        : 'RECOVERED. Search team located the scout. Injured. Medical supplies used. They found some ammo.';
                },
                effect: (res, state) => {
                    const hasAgile = state.family.some(m => m.traits.includes('agile'));
                    if (hasAgile) return { ...res, meds: Math.max(0, res.meds - 1), ammo: res.ammo + 8, morale: Math.min(100, res.morale + 14) };
                    return { ...res, meds: Math.max(0, res.meds - 2), ammo: res.ammo + 5, morale: Math.min(100, res.morale + 8), fuel: Math.max(0, res.fuel - 4) };
                }
            },
            {
                id: 'wait_out',
                label: 'Hold Position — Wait for Return',
                log: () => 'WAITED. Scout returned after dark, shaken but alive. Unit morale strained by the uncertainty.',
                effect: (res) => ({ ...res, morale: Math.max(0, res.morale - 16), food: Math.max(0, res.food - 4) })
            }
        ]
    },
    {
        id: 'scavenger_intel',
        name: 'INTEL_RECEIVED',
        description: 'A passing survivor traded information: a nearby location with untouched food stores. Risk unknown. Window closing.',
        samanthaQuote: "Commander, I'm cross-referencing the coordinates. The area hasn't been mapped. Could be a trap. Could be gold.",
        probability: 0.07,
        minDay: 6,
        choices: [
            {
                id: 'run_the_intel',
                label: 'Run the Op — Send Team Immediately',
                log: (res, state) => {
                    const hasHunter = state.family.some(m => m.traits.includes('hunter'));
                    const hasAgile  = state.family.some(m => m.traits.includes('agile'));
                    const roll = Math.random();
                    if (hasHunter || hasAgile) return 'SUCCESSFUL RUN. Specialist made it count. Significant food secured. Worth the risk.';
                    if (roll > 0.4) return 'RECOVERED. Intel was accurate. Food and water secured from the cache.';
                    return 'AMBUSH. Intel was bait. Team returned empty-handed and shaken. Hostile area.';
                },
                effect: (res, state) => {
                    const hasHunter = state.family.some(m => m.traits.includes('hunter'));
                    const hasAgile  = state.family.some(m => m.traits.includes('agile'));
                    if (hasHunter || hasAgile) return { ...res, food: res.food + 26, water: res.water + 14, fuel: Math.max(0, res.fuel - 4) };
                    const roll = Math.random();
                    if (roll > 0.4) return { ...res, food: res.food + 18, water: res.water + 10, fuel: Math.max(0, res.fuel - 5) };
                    return { ...res, morale: Math.max(0, res.morale - 22), ammo: Math.max(0, res.ammo - 6), fuel: Math.max(0, res.fuel - 6) };
                }
            },
            {
                id: 'verify_first',
                label: 'Verify From a Distance — Recon Only',
                log: () => 'VERIFIED. Team confirmed the location is real. Logged for a controlled extraction op tomorrow.',
                effect: (res) => ({ ...res, fuel: Math.max(0, res.fuel - 3), morale: Math.max(0, res.morale - 3) })
            }
        ]
    },
    {
        id: 'good_day',
        name: 'ATMOSPHERIC_CLEAR',
        description: 'Unusually clear conditions. Minimal threat indicators. Unit is rested and ready.',
        samanthaQuote: "Commander, all systems nominal. Threat indicators at historic lows. A rare window of opportunity.",
        probability: 0.08,
        minDay: 1,
        choices: [
            {
                id: 'maximize',
                label: 'Maximize Productivity — Full Operations',
                log: () => 'PRODUCTIVE DAY. Clear conditions leveraged fully. Supplies gathered. Morale elevated.',
                effect: (res) => ({ ...res, food: res.food + 14, water: res.water + 10, morale: Math.min(100, res.morale + 16) })
            },
            {
                id: 'rest_day',
                label: 'Rest and Recuperate — Full Recovery',
                log: () => 'REST PROTOCOL. Unit fully recuperated. Morale significantly restored. Ready for what comes next.',
                effect: (res) => ({ ...res, morale: Math.min(100, res.morale + 30) })
            }
        ]
    }
];

const initialState = {
    step: 'SAMANTHA',
    user: null,
    day: 1,
    hours: 0,   // hours elapsed today (0–23); advances day at 24
    points: 0,

    map: {
        grid: [],
        position: { x: 7, y: 7 },
        selectedTile: null
    },

    legacy: {
        totalPoints: 0,
        bestDay: 0,
        masteryLevel: 0,
        timelineCount: 0,
        unlockedPerks: [],
        achievements: [],
        hasSeenMapTutorial: false,
        hasSeenSurvivalTutorial: false
    },

    family: [],
    compound: null,

    resources: {
        food: 80,
        water: 80,
        fuel: 15,
        defense: 0,
        morale: 85,
        meds: 3,
        ammo: 20,
        budget: 50000
    },

    pendingEvent: null,

    logs: ["SURVIVAL_TERMINAL_OS_V1.0 INITIALIZED", "SHELTER LOGIC: ONLINE"],
    isPostWar: false,

    preservedFamily: null,
    deceased: [],
    usedQuestionIds: [],
    savedSetup: null,   // { family, compound, resources } — persists in cc_setup_v1, survives patch resets
};

// --- Storage Utilities ---
const STORAGE_KEY = 'cc_v4_persistent_state';
const loadLocalState = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved) : null;
    } catch (e) {
        console.error("FAILED_TO_LOAD_LOCAL_RECORDS", e);
        return null;
    }
};

const saveLocalState = (state) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        if (state.user) {
            localStorage.setItem(`cc_v4_state_${state.user}`, JSON.stringify(state));
        }
    } catch (e) {
        console.error("FAILED_TO_SYNC_LOCAL_RECORDS", e);
    }
};

function rollForEvent(state) {
    const eligible = RANDOM_EVENTS.filter(e =>
        state.day >= e.minDay &&
        (!e.conditions || e.conditions(state))
    );

    const shuffled = eligible.sort(() => Math.random() - 0.5);

    // THREAT multiplier: higher day = more frequent crises (Oregon Trail style)
    const threatMultiplier = state.day < 15 ? 1.0 :
                             state.day < 30 ? 1.5 :
                             state.day < 45 ? 2.2 : 3.0;

    const roll = Math.random();
    let cumulative = 0;
    for (const event of shuffled) {
        cumulative += event.probability * threatMultiplier;
        if (roll < cumulative) return event;
    }
    return null;
}

function checkAchievements(state) {
    const unlocked = state.legacy.achievements || [];
    const newUnlocked = [];
    const logs = [];

    ACHIEVEMENTS.forEach(ach => {
        if (!unlocked.includes(ach.id) && ach.condition(state)) {
            newUnlocked.push(ach.id);
            logs.push(`✦ ACHIEVEMENT UNLOCKED: ${ach.name.toUpperCase()} — ${ach.description}`);
        }
    });

    return { newUnlocked, logs };
}

function gameReducer(state, action) {
    switch (action.type) {
        case 'LOAD_STATE':
            return { ...state, ...action.payload };

        case 'START_GAME':
            return {
                ...state,
                step: 'SURVIVE',
                map: { ...state.map, grid: generateMap() },
                savedSetup: { family: state.family, compound: state.compound, resources: state.resources },
                logs: ["LOCKDOWN INITIATED. WORLD STATUS: CRITICAL.", ...state.logs]
            };

        case 'DISMISS_MAP_TUTORIAL':
            return { ...state, legacy: { ...state.legacy, hasSeenMapTutorial: true } };

        case 'DISMISS_SURVIVAL_TUTORIAL':
            return { ...state, legacy: { ...state.legacy, hasSeenSurvivalTutorial: true } };

        case 'SELECT_TILE':
            return { ...state, map: { ...state.map, selectedTile: action.payload } };

        case 'SCAN_TILE': {
            const { x, y } = action.payload;
            const scannedGrid = state.map.grid.map(row =>
                row.map(tile => (tile.x === x && tile.y === y) ? { ...tile, scanned: true } : tile)
            );
            return {
                ...state,
                map: { ...state.map, grid: scannedGrid, selectedTile: { ...state.map.selectedTile, scanned: true } },
                logs: [`SCAN: Sector [${x},${y}] identified as ${state.map.grid[y][x].type}.`, ...state.logs]
            };
        }

        case 'LOOT_TILE': {
            const { lx, ly } = action.payload;
            const lootedGrid = state.map.grid.map(row =>
                row.map(tile => (tile.x === lx && tile.y === ly) ? { ...tile, looted: true, type: 'EMPTY' } : tile)
            );
            return {
                ...state,
                map: { ...state.map, grid: lootedGrid, selectedTile: { ...state.map.selectedTile, looted: true, type: 'EMPTY' } }
            };
        }

        case 'UPDATE_SETUP':
            return { ...state, ...action.payload };

        case 'PERFORM_ACTION': {
            const { reward, actionType, hours: hoursSpent = 6 } = action.payload;

            const hasSpouse = state.family.some(m => m.role === 'SPOUSE');
            const effectiveHoursSpent = hasSpouse ? hoursSpent * 0.85 : hoursSpent;

            // --- Hourly time advancement ---
            const currentHours = state.hours || 0;
            const totalHoursElapsed = currentHours + effectiveHoursSpent;
            const daysToAdvance = Math.floor(totalHoursElapsed / 24);
            const newHours = totalHoursElapsed % 24;
            const newDay = state.day + daysToAdvance;
            const hoursFraction = effectiveHoursSpent / 24;

            // --- D&D Style Outcome Roll (d20) ---
            const d20 = Math.floor(Math.random() * 20) + 1;
            let rollMultiplier = 1.0;
            let rollNote = '';
            if (d20 === 1)       { rollMultiplier = 0.05; rollNote = ' ⚡ CRITICAL FUMBLE'; }
            else if (d20 <= 4)   { rollMultiplier = 0.45; rollNote = ' [Setback]'; }
            else if (d20 <= 14)  { rollMultiplier = 1.0; }
            else if (d20 <= 19)  { rollMultiplier = 1.45; rollNote = ' [Solid Result]'; }
            else                 { rollMultiplier = 2.2;  rollNote = ' ⚡ CRITICAL SUCCESS'; }

            // --- Per-person trait-based daily consumption ---
            let totalFood = 0;
            let totalWater = 0;
            let defenseBonus = 0;
            let moraleDrainMultiplier = 1.0;

            state.family.forEach(m => {
                let pFood = 3.8;
                let pWater = 4.6;

                if (m.role === 'SPOUSE') {
                    pFood = 1.0; // Spouse is highly efficient as per FEATURES.md
                    pWater = 1.0;
                } else {
                    if (m.traits.includes('frugal')) pFood *= 0.72;
                    if (m.traits.includes('strong')) pFood *= 1.18;
                    if (m.traits.includes('mechanic')) pFood *= 0.95;
                    
                    // Age-based modifiers
                    if (m.ageCategory === 'INFANT') {
                        pFood *= 1.5; // Infants are resource-heavy
                        pWater *= 1.5;
                        moraleDrainMultiplier *= 0.8; // Infants reduce stress/morale drain
                    } else if (m.ageCategory === 'CHILD') {
                        pFood *= 0.5;
                        pWater *= 0.5;
                    } else if (m.ageCategory === 'TEEN') {
                        pFood *= 0.8;
                        pWater *= 0.8;
                        defenseBonus += 1; // Teens help with defense
                    }
                }

                totalFood += pFood;
                totalWater += pWater;
            });

            const hasLeader = state.family.some(m => m.traits.includes('leader'));
            const hasMechanic = state.family.some(m => m.traits.includes('mechanic'));

            const masteryBonus = Math.min(0.4, state.legacy.masteryLevel * 0.05);
            const efficiency = (state.compound?.stats?.shelter || 1) / 5;

            const dayTier = state.day < 7  ? 1.0 :
                            state.day < 18 ? 1.0 + (state.day - 7)  / 11 * 0.45 :
                            state.day < 32 ? 1.45 + (state.day - 18) / 14 * 0.45 :
                            state.day < 50 ? 1.9  + (state.day - 32) / 18 * 0.35 :
                            2.25;

            const efficiencyFactor = Math.max(0.3, 1.2 - (efficiency * 0.4) - masteryBonus);
            const perks = state.legacy.unlockedPerks || [];
            const consumptionMultiplier = perks.includes('NUTRIENT_EFFICIENCY') ? 0.9 : 1.0;

            // Scale daily consumption by hours actually spent
            const actionFood  = totalFood  * efficiencyFactor * dayTier * hoursFraction * consumptionMultiplier;
            const actionWater = totalWater * efficiencyFactor * dayTier * hoursFraction * consumptionMultiplier;
            const actionFuel  = state.family.length * (hasMechanic ? 0.28 : 0.42) * hoursFraction;
            const actionMoraleDrain = state.family.length * 1.6 * (hasLeader ? 0.65 : 1.0) * hoursFraction * moraleDrainMultiplier;

            // --- Trait & Perk adjustments to reward ---
            const hasHunter = state.family.some(m => m.traits.includes('hunter'));
            const hasAgile  = state.family.some(m => m.traits.includes('agile'));
            const hasTeen   = state.family.some(m => m.ageCategory === 'TEEN');
            const hasScavengePerk = perks.includes('SCAVENGE_PRIORITY');
            const hasCommandPerk  = perks.includes('COMMAND_AUTHORITY');

            const adjustedReward = { ...reward };
            let scavengeMultiplier = 1.0;
            if (hasHunter) scavengeMultiplier += 0.4;
            if (hasAgile)  scavengeMultiplier += 0.15;
            if (hasTeen)   scavengeMultiplier += 0.10;
            if (hasScavengePerk) scavengeMultiplier += 0.15;

            const isScavengeAction = actionType.toLowerCase().includes('scavenge') ||
                                     actionType.toLowerCase().includes('loot') ||
                                     actionType.toLowerCase().includes('hunt') ||
                                     actionType.toLowerCase().includes('fuel run');

            if (reward.food  && isScavengeAction) adjustedReward.food  = reward.food  * scavengeMultiplier;
            if (reward.water && isScavengeAction) adjustedReward.water = reward.water * scavengeMultiplier;
            if (actionType === 'Family Strategy Session' && hasCommandPerk) {
                adjustedReward.morale = (reward.morale || 0) * 2;
            }

            // Apply D&D roll to positive reward values only
            Object.keys(adjustedReward).forEach(k => {
                if (adjustedReward[k] > 0) {
                    adjustedReward[k] = adjustedReward[k] * rollMultiplier * (0.85 + Math.random() * 0.3);
                }
            });

            const nextResources = {
                ...state.resources,
                food:    Math.max(0, state.resources.food    - actionFood  + (adjustedReward.food  || 0)),
                water:   Math.max(0, state.resources.water   - actionWater + (adjustedReward.water || 0)),
                fuel:    Math.max(0, state.resources.fuel    - actionFuel  + (adjustedReward.fuel  || 0)),
                defense: Math.max(0, state.resources.defense + defenseBonus + (adjustedReward.defense || 0)),
                morale:  Math.max(0, Math.min(100, state.resources.morale - actionMoraleDrain + (adjustedReward.morale || 0))),
                meds:    Math.max(0, state.resources.meds    + (adjustedReward.meds  || 0)),
                ammo:    Math.max(0, state.resources.ammo    + (adjustedReward.ammo  || 0))
            };

            const intermediateState = { ...state, resources: nextResources };
            const { newUnlocked, logs: achLogs } = checkAchievements(intermediateState);

            // Individual Death Logic (Oregon Trail Style)
            let newFamily = [...state.family];
            let newDeceased = [...(state.deceased || [])];
            let deathLogs = [];

            if (nextResources.food <= 0 || nextResources.water <= 0) {
                newFamily = state.family.filter((member) => {
                    const killChance = (nextResources.food <= 0 ? 0.1 : 0) + (nextResources.water <= 0 ? 0.15 : 0);
                    if (Math.random() < killChance) {
                        newDeceased.push({ ...member, cause: nextResources.water <= 0 ? 'Dehydration' : 'Starvation', day: state.day });
                        deathLogs.push(`⚠ ${member.name} has perished from ${nextResources.water <= 0 ? 'dehydration' : 'starvation'}. The compound is quieter now.`);
                        return false;
                    }
                    return true;
                });
            }

            const commanderDied = state.family.length > 0 && !newFamily.find(m => m.name === state.family[0].name);
            const unitWiped     = newFamily.length === 0;
            const criticalFailure = commanderDied || unitWiped || nextResources.morale <= 0;

            if (criticalFailure) {
                let failReason = "The compound fell silent. Nobody was left to write the rest.";
                if (nextResources.morale <= 0) failReason = "The unit broke. Everyone walked out in the night. The compound is empty.";
                else if (commanderDied) failReason = `${state.family[0].name} is gone. Without a Commander, the unit collapsed.`;
                else if (unitWiped) failReason = "The last entry reads: we didn't make it.";

                const newTotalPoints = state.legacy.totalPoints + state.points;
                const newMastery = Math.floor(newTotalPoints / 10000);
                return {
                    ...state,
                    resources: nextResources,
                    family: newFamily,
                    deceased: newDeceased,
                    hours: newHours,
                    step: 'DEAD',
                    pendingEvent: null,
                    preservedFamily: state.family,
                    legacy: {
                        ...state.legacy,
                        totalPoints: newTotalPoints,
                        bestDay: Math.max(state.legacy.bestDay, state.day),
                        masteryLevel: newMastery,
                        timelineCount: state.legacy.timelineCount + 1
                    },
                    logs: [...deathLogs, failReason, ...state.logs]
                };
            }

            // --- Journal-style log entries ---
            const timeStr = `Day ${state.day}, ${String(currentHours).padStart(2, '0')}:00`;
            let detailLog = '';
            if (adjustedReward.food  !== undefined && adjustedReward.food !== 0) detailLog += `${adjustedReward.food > 0 ? '+' : ''}${Math.floor(adjustedReward.food)} food. `;
            if (adjustedReward.water !== undefined && adjustedReward.water !== 0) detailLog += `${adjustedReward.water > 0 ? '+' : ''}${Math.floor(adjustedReward.water)} water. `;
            if (adjustedReward.defense !== undefined && adjustedReward.defense !== 0) detailLog += `Defense ${adjustedReward.defense > 0 ? '+' : ''}${adjustedReward.defense}. `;
            if (adjustedReward.morale !== undefined && adjustedReward.morale !== 0) detailLog += `Morale ${adjustedReward.morale > 0 ? '+' : ''}${Math.floor(adjustedReward.morale)}. `;
            if (adjustedReward.meds !== undefined && adjustedReward.meds !== 0) detailLog += `${adjustedReward.meds > 0 ? '+' : ''}${adjustedReward.meds} meds. `;
            if (adjustedReward.ammo !== undefined && adjustedReward.ammo !== 0) detailLog += `${adjustedReward.ammo > 0 ? '+' : ''}${adjustedReward.ammo} ammo. `;
            if (adjustedReward.fuel !== undefined && adjustedReward.fuel !== 0) detailLog += `${adjustedReward.fuel > 0 ? '+' : ''}${Math.floor(adjustedReward.fuel)} fuel. `;

            const actionLog = `[${timeStr}]${rollNote} — ${actionType}: ${detailLog || "Operational adjustments."}`;
            const consumptionLog = `  └ consumed ${actionFood.toFixed(1)} food, ${actionWater.toFixed(1)} water over ${hoursSpent}h.`;

            // Perk Unlocks
            let updatedUnlockedPerks = [...perks];
            let perkUnlockLog = null;
            const checkUnlock = (dayThreshold, perkId, perkName) => {
                if (newDay >= dayThreshold && !updatedUnlockedPerks.includes(perkId)) {
                    updatedUnlockedPerks.push(perkId);
                    perkUnlockLog = `✦ Legacy perk unlocked: ${perkName} — permanent across all timelines.`;
                }
            };
            checkUnlock(10, 'SCAVENGE_PRIORITY', 'Scavenge Priority');
            checkUnlock(20, 'NUTRIENT_EFFICIENCY', 'Nutrient Efficiency');
            checkUnlock(35, 'REINFORCED_START', 'Reinforced Start');
            checkUnlock(50, 'NEURAL_MASTERY', 'Neural Mastery');

            const triggeredEvent = rollForEvent({ ...state, day: newDay });
            const eventLog = triggeredEvent ? `⚠ ${triggeredEvent.name} — ${triggeredEvent.description}` : null;

            const newLogs = [
                ...(perkUnlockLog ? [perkUnlockLog] : []),
                actionLog,
                consumptionLog,
                ...(eventLog ? [eventLog] : []),
                ...state.logs
            ].slice(0, 100);

            return {
                ...state,
                day: newDay,
                hours: newHours,
                family: newFamily,
                deceased: newDeceased,
                resources: nextResources,
                points: state.points + (state.day * state.family.length * 10),
                pendingEvent: triggeredEvent || null,
                legacy: {
                    ...state.legacy,
                    unlockedPerks: updatedUnlockedPerks,
                    achievements: [...(state.legacy.achievements || []), ...newUnlocked]
                },
                logs: [...achLogs, ...deathLogs, ...newLogs]
            };
        }

        case 'RESOLVE_EVENT': {
            const { choiceId } = action.payload;
            const event = state.pendingEvent;
            if (!event) return state;

            const choice = event.choices.find(c => c.id === choiceId);
            if (!choice) return state;

            const newResources = choice.effect(state.resources, state);
            const choiceLog = choice.log(state.resources, state);

            // Individual Death Logic for Events
            let newFamily = [...state.family];
            let newDeceased = [...(state.deceased || [])];
            let deathLogs = [];

            if (newResources.food <= 0 || newResources.water <= 0) {
                newFamily = state.family.filter((member) => {
                    const killChance = (newResources.food <= 0 ? 0.1 : 0) + (newResources.water <= 0 ? 0.15 : 0);
                    if (Math.random() < killChance) {
                        newDeceased.push({ ...member, cause: newResources.water <= 0 ? 'Dehydration' : 'Starvation', day: state.day });
                        deathLogs.push(`SYSTEM_ALERT: ${member.name.toUpperCase()} has perished from ${newResources.water <= 0 ? 'dehydration' : 'starvation'}.`);
                        return false;
                    }
                    return true;
                });
            }

            const commanderDied = state.family.length > 0 && !newFamily.find(m => m.name === state.family[0].name);
            const unitWiped = newFamily.length === 0;
            const criticalFailure = commanderDied || unitWiped || newResources.morale <= 0;
            const { newUnlocked, logs: achLogs } = checkAchievements({ ...state, resources: newResources });

            if (criticalFailure) {
                let failReason = "CRITICAL FAILURE. TIMELINE COLLAPSED.";
                if (newResources.morale <= 0) failReason = "CRITICAL: MORALE COLLAPSE. UNIT DESERTED.";
                else if (commanderDied) failReason = `CRITICAL: COMMANDER ${state.family[0].name.toUpperCase()} HAS PERISHED.`;
                else if (unitWiped) failReason = "CRITICAL: UNIT WIPED OUT.";

                const newTotalPoints = state.legacy.totalPoints + state.points;
                const newMastery = Math.floor(newTotalPoints / 10000);
                return {
                    ...state,
                    resources: newResources,
                    family: newFamily,
                    deceased: newDeceased,
                    step: 'DEAD',
                    pendingEvent: null,
                    preservedFamily: state.family,
                    legacy: {
                        ...state.legacy,
                        totalPoints: newTotalPoints,
                        bestDay: Math.max(state.legacy.bestDay, state.day),
                        masteryLevel: newMastery,
                        timelineCount: state.legacy.timelineCount + 1
                    },
                    logs: [choiceLog, ...deathLogs, failReason, ...state.logs]
                };
            }

            return {
                ...state,
                resources: newResources,
                family: newFamily,
                deceased: newDeceased,
                pendingEvent: null,
                legacy: {
                    ...state.legacy,
                    achievements: [...(state.legacy.achievements || []), ...newUnlocked]
                },
                logs: [choiceLog, ...achLogs, ...deathLogs, ...state.logs].slice(0, 100)
            };
        }

        case 'RESET_LOOP':
            const startPerks = state.legacy.unlockedPerks || [];
            const hasReinforced = startPerks.includes('REINFORCED_START');
            return {
                ...initialState,
                user: state.user,
                legacy: state.legacy,
                hours: 0,
                preservedFamily: state.family.length > 0 ? state.family : state.preservedFamily,
                resources: {
                    ...initialState.resources,
                    fuel: initialState.resources.fuel + (hasReinforced ? 50 : 0),
                    defense: initialState.resources.defense + (hasReinforced ? 5 : 0),
                    budget: initialState.resources.budget + (state.legacy.masteryLevel * 5000)
                },
                usedQuestionIds: [],
                deceased: [],
                pendingEvent: null,
                logs: [`Timeline ${state.legacy.timelineCount + 1} initialized. Starting over.`, `Mastery level ${state.legacy.masteryLevel} active.`, ...state.logs]
            };

        case 'RETRY_SAME_FAMILY':
            return {
                ...initialState,
                user: state.user,
                legacy: state.legacy,
                family: state.preservedFamily || [],
                preservedFamily: state.preservedFamily,
                deceased: [],
                step: 'SETUP',
                pendingEvent: null,
                logs: ["TIMELINE REWOUND. RE-DEPLOYING PREVIOUS UNIT.", ...state.logs]
            };

        case 'RETRY_EDIT_FAMILY':
            return {
                ...initialState,
                user: state.user,
                legacy: state.legacy,
                family: state.preservedFamily || [],
                preservedFamily: state.preservedFamily,
                deceased: [],
                step: 'SETUP',
                setupType: 'EDIT',
                pendingEvent: null,
                logs: ["TIMELINE REWOUND. OPENING UNIT CONFIGURATION.", ...state.logs]
            };

        case 'RESTART_WITH_SAVED_SETUP': {
            const setup = state.savedSetup;
            if (!setup) return { ...state, step: 'SETUP' };
            const rstPerks = state.legacy.unlockedPerks || [];
            const rstReinforced = rstPerks.includes('REINFORCED_START');
            return {
                ...initialState,
                user: state.user,
                legacy: state.legacy,
                savedSetup: state.savedSetup,
                family: setup.family,
                compound: setup.compound,
                preservedFamily: setup.family,
                resources: {
                    ...setup.resources,
                    fuel: setup.resources.fuel + (rstReinforced ? 50 : 0),
                    defense: setup.resources.defense + (rstReinforced ? 5 : 0),
                    budget: setup.resources.budget + (state.legacy.masteryLevel * 5000),
                },
                step: 'SURVIVE',
                map: { ...initialState.map, grid: generateMap() },
                deceased: [],
                pendingEvent: null,
                logs: [`Redeploying unit. Timeline ${(state.legacy.timelineCount || 0) + 1} initialized.`]
            };
        }

        case 'RESTORE_SAVED_UNIT': {
            const roster = state.savedSetup?.family;
            if (!roster) return state;
            return {
                ...initialState,
                user: state.user,
                legacy: state.legacy,
                savedSetup: state.savedSetup,
                family: roster,
                preservedFamily: roster,
                step: 'SETUP',
                logs: ["ROSTER_RESTORED: Unit records re-synchronized. Proceed to Sector Selection.", ...state.logs]
            };
        }

        case 'SAVE_STATE':
            return state;

        case 'LOGIN':
            return { ...state, user: action.payload, step: 'SAMANTHA' };

        case 'FINISH_CHAT':
            return { ...state, step: 'SETUP' };

        case 'ADD_MEMBER':
            return { ...state, family: [...state.family, action.payload] };

        case 'CLEAR_FAMILY':
            return { ...state, family: [] };

        case 'UPDATE_USED_QUESTIONS':
            return { ...state, usedQuestionIds: [...state.usedQuestionIds, ...action.payload] };

        case 'LOGOUT':
            return initialState;

        default:
            return state;
    }
}

export class GlobalErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="h-screen w-screen flex flex-col items-center justify-center p-8 bg-[#1c1917] text-[#f59e0b] mono">
                    <h1 className="text-2xl font-black mb-4 uppercase tracking-tighter">System Malfunction</h1>
                    <div className="bg-[#292524] border border-[#f59e0b]/30 p-6 rounded-lg max-w-2xl w-full">
                        <p className="mb-4 font-bold">{'>'} Diagnostic Data:</p>
                        <pre className="text-xs whitespace-pre-wrap">{this.state.error?.toString()}</pre>
                    </div>
                    <button onClick={() => window.location.reload()} className="mt-8 border border-[#f59e0b] px-6 py-2 rounded hover:bg-[#f59e0b] hover:text-black transition-colors">REBOOT_OS</button>
                </div>
            );
        }
        return this.props.children;
    }
}

export default function App() {
    const [loginInput, setLoginInput] = useState('');
    const [gameState, dispatch] = useReducer(gameReducer, initialState, (defaultState) => {
        const local = loadLocalState();
        const base = local ? { ...defaultState, ...local } : { ...defaultState };
        // Load setup roster from its own stable key — survives main storage key changes between patches
        if (!base.savedSetup) {
            try {
                const roster = localStorage.getItem('cc_setup_v1');
                if (roster) base.savedSetup = JSON.parse(roster);
            } catch (e) {}
        }
        return base;
    });
    const prevStepRef = React.useRef(gameState.step);

    // Auto-save on every state change
    useEffect(() => {
        saveLocalState(gameState);
        if (gameState.user) {
            saveGameStateToCloud(gameState.user, gameState).catch(err => {
                console.warn("CLOUD_SYNC_INTERRUPTED", err);
            });
        }
    }, [gameState]);

    // Keep setup roster in its own stable key (survives patch resets)
    useEffect(() => {
        if (gameState.savedSetup) {
            localStorage.setItem('cc_setup_v1', JSON.stringify(gameState.savedSetup));
        }
    }, [gameState.savedSetup]);

    // Save leaderboard entry when a run ends
    useEffect(() => {
        if (gameState.step === 'DEAD' && prevStepRef.current !== 'DEAD') {
            try {
                const board = JSON.parse(localStorage.getItem('cc_leaderboard_v3') || '[]');
                board.push({
                    user: gameState.user,
                    day: gameState.day,
                    points: gameState.legacy.totalPoints,
                    mastery: gameState.legacy.masteryLevel,
                    date: new Date().toISOString().split('T')[0]
                });
                board.sort((a, b) => b.points - a.points);
                localStorage.setItem('cc_leaderboard_v3', JSON.stringify(board.slice(0, 20)));
            } catch (e) {}
        }
        prevStepRef.current = gameState.step;
    }, [gameState.step]);

    useEffect(() => {
        const savedUser = localStorage.getItem('cc_user');
        if (savedUser) performLogin(savedUser);
    }, []);

    const performLogin = async (uname) => {
        const uppercaseName = uname.trim().toUpperCase();
        if (uppercaseName.length > 2) {
            // Check if we already have this user's state loaded from the main persistent key
            if (gameState.user === uppercaseName && gameState.family && gameState.family.length > 0) {
                // Already loaded and has a unit, just ensure we aren't stuck in SAMANTHA if not intended
                if (gameState.step === 'SAMANTHA') {
                    dispatch({ type: 'UPDATE_SETUP', payload: { step: 'SETUP' } });
                }
                return;
            }

            const cloudState = await loadGameStateFromCloud(uppercaseName);
            if (cloudState) {
                // If they already have a family, don't force them back to the intro chat
                if (cloudState.step === 'SAMANTHA' && cloudState.family && cloudState.family.length > 0) {
                    cloudState.step = 'SETUP';
                }
                dispatch({ type: 'LOAD_STATE', payload: cloudState });
                localStorage.setItem('cc_user', uppercaseName);
                return;
            }

            const savedState = localStorage.getItem(`cc_v4_state_${uppercaseName}`);
            if (savedState) {
                const parsed = JSON.parse(savedState);
                if (parsed.step === 'SAMANTHA' && parsed.family && parsed.family.length > 0) {
                    parsed.step = 'SETUP';
                }
                dispatch({ type: 'LOAD_STATE', payload: parsed });
                localStorage.setItem('cc_user', uppercaseName);
            } else {
                dispatch({ type: 'LOGIN', payload: uppercaseName });
                localStorage.setItem('cc_user', uppercaseName);
            }
        }
    };

    const handleLogin = (e) => {
        e.preventDefault();
        performLogin(loginInput);
    };

    const handleGoogleLogin = async () => {
        try {
            const user = await signInWithGoogle();
            const uname = user.displayName?.toUpperCase().replace(/\s+/g, '_') || user.uid.substring(0, 8);
            performLogin(uname);
        } catch (error) {
            alert("Google Auth Failed: " + error.message);
        }
    };

    const handleFacebookLogin = async () => {
        try {
            const user = await signInWithFacebook();
            const uname = user.displayName?.toUpperCase().replace(/\s+/g, '_') || user.uid.substring(0, 8);
            performLogin(uname);
        } catch (error) {
            alert("Facebook Auth Failed: " + error.message);
        }
    };

    const clearLocalData = () => {
        localStorage.clear();
        window.location.reload();
    };

    const handleLogout = () => {
        localStorage.removeItem('cc_user');
        dispatch({ type: 'LOGOUT' });
    };

    if (!gameState.user) {
        return (
            <div className="h-screen w-screen flex flex-col items-center justify-center p-4 bg-[#1c1917]">
                <div className="glass p-8 rounded-xl border-l-4 border-[#f59e0b] w-full max-w-md animate-fade-in shadow-2xl">
                    <h1 className="text-3xl font-black mb-2 uppercase tracking-tighter text-white glitch">COMPOUND_COMMANDER</h1>
                    <p className="text-[#f59e0b]/60 text-xs mb-8 mono uppercase tracking-widest font-bold">Civilian Survival Interface</p>
                    <form onSubmit={handleLogin} className="space-y-4">
                        <input
                            type="text"
                            value={loginInput}
                            onChange={(e) => setLoginInput(e.target.value)}
                            className="w-full bg-[#292524] border border-[#44403c] text-[#f59e0b] p-3 rounded mono focus:border-[#f59e0b] outline-none"
                            placeholder="Enter Survivor ID..."
                            autoFocus
                        />
                        <button type="submit" className="w-full bg-[#f59e0b]/20 text-[#f59e0b] border border-[#f59e0b] p-3 rounded mono font-black hover:bg-[#f59e0b] hover:text-black transition-all">
                            INITIALIZE_IDENTITY
                        </button>
                    </form>

                    <div className="mt-6 flex flex-col gap-3">
                        <button
                            type="button"
                            onClick={handleGoogleLogin}
                            className="flex items-center justify-center gap-3 w-full bg-white border border-gray-300 text-gray-700 py-2.5 rounded font-bold text-sm hover:bg-gray-50 transition-all shadow-sm"
                        >
                            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5" alt="Google" />
                            Sign in with Google
                        </button>
                        <button
                            type="button"
                            onClick={handleFacebookLogin}
                            className="flex items-center justify-center gap-3 w-full bg-[#1877F2] text-white py-2.5 rounded font-bold text-sm hover:bg-[#166fe5] transition-all shadow-sm"
                        >
                            <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                            Continue with Facebook
                        </button>
                    </div>
                    <button onClick={clearLocalData} className="w-full mt-8 text-[9px] mono text-rose-500/40 hover:text-rose-500 transition-colors uppercase tracking-widest">
                        Data_Purge (Reset Everything)
                    </button>
                </div>
            </div>
        );
    }

    if (gameState.step === 'DEAD') {
        const causeOfDeath = gameState.resources.food <= 0 ? 'STARVATION' :
                            gameState.resources.water <= 0 ? 'DEHYDRATION' : 'PSYCHOLOGICAL COLLAPSE';
        const leaderboard = (() => { try { return JSON.parse(localStorage.getItem('cc_leaderboard_v3') || '[]'); } catch { return []; } })();
        return (
            <div className="h-screen w-screen flex flex-col items-center justify-center p-4 bg-red-950/20">
                <div className="glass p-12 rounded-xl border-l-4 border-rose-500 text-center max-w-lg shadow-2xl">
                    <h2 className="text-5xl font-black text-white mb-2 glitch uppercase tracking-tighter">Terminated</h2>
                    <p className="text-rose-400/60 text-xs mono mb-6 tracking-widest">CAUSE: {causeOfDeath}</p>
                    <div className="text-rose-400 mono text-sm mb-8 leading-relaxed space-y-2">
                        <p>TIMELINE SURVIVED: <span className="text-white font-bold">{gameState.day} DAYS</span></p>
                        <p>LEGACY SCORE: <span className="text-white font-bold">{gameState.legacy.totalPoints.toLocaleString()}</span></p>
                        <p>BEST RUN: <span className="text-white font-bold">{gameState.legacy.bestDay} DAYS</span></p>
                        <p className="text-[#f59e0b] font-bold border-t border-[#f59e0b]/20 pt-4">MASTERY LEVEL: {gameState.legacy.masteryLevel}</p>
                        <p className="text-[10px] opacity-60">Each mastery level grants permanent budget and efficiency bonuses.</p>

                        {gameState.legacy.unlockedPerks?.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-[#f59e0b]/20 text-left">
                                <p className="text-[9px] uppercase tracking-widest opacity-40 mb-2 text-[#f59e0b]">Active Legacy Perks:</p>
                                <div className="flex flex-wrap gap-2">
                                    {gameState.legacy.unlockedPerks.map((perk, i) => (
                                        <span key={i} className="text-[9px] bg-[#f59e0b]/10 border border-[#f59e0b]/30 px-2 py-1 rounded text-[#f59e0b] font-bold">✦ {perk}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {gameState.family.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-rose-500/20 text-left">
                                <p className="text-[9px] uppercase tracking-widest opacity-40 mb-2">Preserved Unit Data:</p>
                                <div className="flex flex-wrap gap-2">
                                    {gameState.family.map((m, i) => (
                                        <span key={i} className="text-[10px] bg-rose-500/10 border border-rose-500/20 px-2 py-1 rounded text-white">{m.name} {m.age ? `(${m.age})` : ''}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {leaderboard.length > 0 && (
                        <div className="mt-6 pt-4 border-t border-[#f59e0b]/20 text-left">
                            <p className="text-[9px] uppercase tracking-widest opacity-40 mb-3 text-[#f59e0b]">Commander Scoreboard — All Runs (This Device)</p>
                            <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
                                {leaderboard.map((entry, i) => (
                                    <div key={i} className={`flex justify-between text-[10px] mono px-2 py-1 rounded ${entry.user === gameState.user ? 'bg-[#f59e0b]/10 text-[#f59e0b]' : 'text-slate-500'}`}>
                                        <span>#{i + 1} {entry.user}</span>
                                        <span>Day {entry.day} · {entry.points.toLocaleString()} pts · ML{entry.mastery}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-3 mt-6">
                        {gameState.savedSetup && (
                            <button
                                onClick={() => dispatch({ type: 'RESTART_WITH_SAVED_SETUP' })}
                                className="bg-emerald-600 text-white px-8 py-4 rounded-xl mono font-black text-sm hover:bg-emerald-500 transition-all shadow-lg border-2 border-emerald-400/40"
                            >
                                ⚡ QUICK RESTART
                                <div className="text-[10px] font-normal opacity-70 mt-0.5 tracking-widest">Same unit · Same compound · Same plan</div>
                            </button>
                        )}
                        <button
                            onClick={() => dispatch({ type: 'RETRY_SAME_FAMILY' })}
                            className="bg-[#f59e0b] text-black px-8 py-3 rounded-xl mono font-black text-sm hover:bg-white transition-all shadow-lg"
                        >
                            CHANGE SETUP
                            <div className="text-[10px] font-normal opacity-60 mt-0.5">Same unit · Pick new compound &amp; plan</div>
                        </button>
                        <button
                            onClick={() => dispatch({ type: 'RESET_LOOP' })}
                            className="bg-[#292524] text-white/60 px-8 py-3 rounded-xl mono font-black text-sm hover:bg-rose-500 hover:text-white transition-all border border-white/10"
                        >
                            Full Reset (New Unit)
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const threatLevel = gameState.day < 15 ? 'LOW' : gameState.day < 30 ? 'ELEVATED' : gameState.day < 45 ? 'CRITICAL' : 'EXTREME';
    const threatColor = gameState.day < 15 ? 'text-emerald-400' : gameState.day < 30 ? 'text-amber-400' : 'text-rose-400 animate-pulse';

    return (
        <div className="h-screen w-full flex flex-col relative z-10 p-2 md:p-4 gap-4 bg-[#1c1917] text-[#f59e0b] overflow-hidden">
            <header className="glass rounded-lg border border-white/5 p-3 flex justify-between items-center shadow-lg flex-wrap gap-2">
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <div className="w-8 h-8 rounded bg-[#f59e0b] flex items-center justify-center font-black text-black text-xs shadow-[0_0_15px_rgba(245,158,11,0.4)]">
                            {gameState.legacy.masteryLevel}
                        </div>
                        {gameState.legacy.unlockedPerks?.length > 0 && (
                            <div className="absolute -top-2 -right-2 flex gap-0.5">
                                {gameState.legacy.unlockedPerks.map((p, i) => (
                                    <div key={i} className="w-2.5 h-2.5 rounded-full bg-sky-500 border border-black shadow-sm" title={p}></div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="font-black tracking-tighter uppercase text-white leading-none flex items-center gap-2">
                            Command Core
                            {gameState.legacy.unlockedPerks?.length > 0 && (
                                <span className="text-[9px] text-sky-400 font-bold animate-pulse">
                                    [PERKS_ACTIVE]
                                </span>
                            )}
                        </div>
                        <div className="text-[10px] mono text-amber-500">OP: {gameState.user} | DAY: {gameState.day} | TL: {gameState.legacy.timelineCount + 1}</div>
                    </div>
                </div>

                <div className="flex gap-4 md:gap-6 mono text-xs">
                    {gameState.step === 'SETUP' ? (
                        <div className="flex flex-col"><span className="text-slate-500 text-[9px]">BUDGET</span><span className="text-emerald-400 font-bold">${gameState.resources.budget.toLocaleString()}</span></div>
                    ) : (
                        <>
                            <div className="flex flex-col"><span className="text-slate-500 text-[9px]">SCORE</span><span className="text-white font-bold">{gameState.points}</span></div>
                            <div className="flex flex-col"><span className="text-slate-500 text-[9px]">FOOD</span><span className={gameState.resources.food < 10 ? 'text-rose-500 animate-pulse font-bold' : 'text-sky-400'}>{Math.floor(gameState.resources.food)}</span></div>
                            <div className="flex flex-col"><span className="text-slate-500 text-[9px]">WATER</span><span className={gameState.resources.water < 10 ? 'text-rose-500 animate-pulse font-bold' : 'text-sky-400'}>{Math.floor(gameState.resources.water)}</span></div>
                            <div className="flex flex-col"><span className="text-slate-500 text-[9px]">MORALE</span><span className={gameState.resources.morale < 25 ? 'text-rose-500 animate-pulse font-bold' : 'text-emerald-400'}>{Math.floor(gameState.resources.morale)}</span></div>
                            <div className="flex flex-col"><span className="text-slate-500 text-[9px]">THREAT</span><span className={`font-bold ${threatColor}`}>{threatLevel}</span></div>
                        </>
                    )}
                </div>

                <div className="flex items-center gap-4">
                    <button onClick={handleLogout} className="text-[10px] mono text-rose-500 border border-rose-500/30 px-3 py-1 rounded hover:bg-rose-500 hover:text-white transition-colors">
                        ABORT
                    </button>
                </div>
            </header>

            <main className="flex-1 relative min-h-0 overflow-hidden">
                {gameState.step === 'SAMANTHA' && <SamanthaTerminal gameState={gameState} dispatch={dispatch} />}
                {gameState.step === 'SETUP' && <SetupFlow gameState={gameState} dispatch={dispatch} />}
                {gameState.step === 'SURVIVE' && <GameView gameState={gameState} dispatch={dispatch} />}
            </main>
        </div>
    );
}
